import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment/moment";
import { Spin } from "antd";
import { InboxOutlined, MessageOutlined } from "@ant-design/icons";
import PanelCard from "../../../../Pages/DashboardPage/components/PanelCard";
import { formatCurrency } from "../../../../assets/helpers";
import "../AllocationCaseDetails.scss";

const EMPTY = "—";

const orNull = (value) =>
    value === null || value === undefined || value === "" ? null : value;

const AgencyFeedback = ({ data }) => {
    const { token } = useSelector((state) => state.auth);
    const { format } = useSelector((state) => state.priceFormat);
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFeedback = async () => {
            if ((!data?.case_id && !data?.id) || !token) return;
            setLoading(true);
            try {
                const response = await axios.get(`portfolio/feedback-details/${data.case_id ? data?.case_id : data?.id}/`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                console.log("Agency feedback API Response:", response.data);

                // Handle different possible structures (array or object containing list)
                const resData = response.data?.data || response.data;
                if (resData) {
                    if (Array.isArray(resData)) {
                        setFeedbacks(resData);
                    } else if (resData.telecaller_feedback && Array.isArray(resData.telecaller_feedback)) {
                        setFeedbacks(resData.telecaller_feedback);
                    } else if (Array.isArray(resData.feedback)) {
                        setFeedbacks(resData.feedback);
                    } else {
                        setFeedbacks([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching agency feedback details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFeedback();
    }, [data?.id, data?.case_id, token]);

    /* Both states used to be inline-styled divs - a bare centred Spin, and an
       #8c8c8c <h3> in a box with no explanation. */
    if (loading) {
        return (
            <div className="caseFeedback">
                <PanelCard title="Feedback" icon={<MessageOutlined />} accent="brand">
                    <div className="caseLoading">
                        <Spin size="large" />
                    </div>
                </PanelCard>
            </div>
        );
    }

    if (!feedbacks || feedbacks.length === 0) {
        return (
            <div className="caseFeedback">
                <PanelCard title="Feedback" icon={<MessageOutlined />} accent="brand">
                    <div className="caseEmpty">
                        <InboxOutlined />
                        <strong>No feedback yet</strong>
                        <span>Telecaller feedback for this case will appear here.</span>
                    </div>
                </PanelCard>
            </div>
        );
    }

    return (
        <div className="caseFeedback">
            {feedbacks.map((item, index) => {
                /* Was MM-DD-YYYY, the only US-ordered date in the app, through a
                   `date ? formatted : '' || "N/A"` expression that only produced
                   "N/A" because '' is falsy. */
                const when = item?.date ? moment(item.date) : null;

                const facts = [
                    {
                        label: "Date",
                        value: when?.isValid() ? when.format("DD MMM YYYY") : null,
                        mono: true,
                    },
                    {
                        label: "Call Status",
                        value: orNull(item.call_status?.value ?? item.call_status),
                    },
                    { label: "Sub-status", value: orNull(item.call_sub_status) },
                    { label: "Attended By", value: orNull(item.call_attend_by) },
                    /* was a hardcoded ₹ prefix, ignoring the price-format setting */
                    {
                        label: "Paid Amount",
                        value: formatCurrency(item.paid_amount, format),
                        mono: true,
                    },
                    {
                        label: "PTP Amount",
                        value: formatCurrency(item.ptp_amount, format),
                        mono: true,
                    },
                    { label: "Bucket ID", value: orNull(item.xbucket), mono: true },
                ];

                /* The prose sits in its own grid, one card per row. Mixed in
                   with the six facts above it would have had to gap: six
                   single cards leave a short second row, and a full-width
                   card cannot fill what is left of it. */
                const remarks = [
                    { label: "Comment", value: orNull(item.comment) },
                    { label: "Notes", value: orNull(item.notes) },
                ];

                return (
                    <PanelCard
                        key={index}
                        title={`Feedback ${index + 1}`}
                        icon={<MessageOutlined />}
                        accent="brand"
                    >
                        <div className="caseGroup">
                            <div className="caseGrid">
                                {facts.map(({ label, value, mono }) => (
                                    <div className="caseField" key={label}>
                                        <span className="caseField__label">{label}</span>
                                        <span
                                            className={`caseField__value${mono ? " is-mono" : ""
                                                }${value ? "" : " is-empty"}`}
                                        >
                                            {value || EMPTY}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="caseGroup">
                            <div className="caseGrid">
                                {remarks.map(({ label, value }) => (
                                    <div className="caseField is-full" key={label}>
                                        <span className="caseField__label">{label}</span>
                                        <span
                                            className={`caseField__value is-prose${value ? "" : " is-empty"
                                                }`}
                                        >
                                            {value || EMPTY}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </PanelCard>
                );
            })}
        </div>
    );
};

export default AgencyFeedback;